import React, { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import DynamicSearchSelect from '../dynamic-select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape of the filter map passed to DynamicSearchSelect's customeFilter */
type FilterMap = Record<string, any>;

/**
 * filterFields accepts:
 *   - string:  `"company"` → watch form field `company`, use `filterKey` prop as API filter key
 *   - object:  `{ company: 'company_id' }` → key = form field to watch, value = API filter key
 */
type FilterFieldsInput = string | Record<string, string>;

/** Normalised entry: which form field to watch → which API filter key to use */
interface FilterFieldEntry {
    /** The form field name to watch (e.g. "company") */
    formField: string;
    /** The API filter key to send (e.g. "company_id") */
    apiKey: string;
}

export interface DynamicDependentFieldProps {
    /** Field name registered in React Hook Form (e.g. "department_id") */
    name: string;

    /**
     * Describes which form field(s) this select depends on.
     *
     * **String** – a single form field name. The API filter key is taken from `filterKey`.
     * @example filterFields="company"
     *
     * **Object** – keys are form field names, values are API filter keys.
     * @example filterFields={{ company: 'company_id', branch: 'branch_id' }}
     */
    filterFields: FilterFieldsInput;

    /**
     * The form array/namespace prefix used in the parent form
     * (same as `fieldArrayName` in DynamicSearchSelect).
     */
    fieldArrayName: string;

    /**
     * API filter key used when `filterFields` is a plain string.
     * Ignored when `filterFields` is an object (the object values are used instead).
     * @default 'company_id'
     */
    filterKey?: string;

    /** Label displayed above the select */
    label?: string;

    /** Placeholder text inside the select */
    placeholder?: string;

    /**
     * API key string forwarded to `DynamicSearchSelect` as `apiType`.
     */
    apiType?: string;

    /**
     * Custom fetch function forwarded to `DynamicSearchSelect` as `customFetchApi`.
     */
    customFetchApi?: (...args: any[]) => Promise<any>;

    /** Disable the field regardless of filter state */
    disabled?: boolean;

    /** React Hook Form validation rules */
    rules?: Record<string, any>;

    /** Allow multi-select (forwarded as `is_multiselect`) */
    isMulti?: boolean;

    /** Mark field as required */
    required?: boolean;

    /** Form control object from useForm */
    control?: any;
    formControl?: any;

    /** Watch function from useForm */
    watch?: any;

    /** SetValue function from useForm */
    setValue?: any;

    /** GetFieldState function from useForm */
    getFieldState?: any;

    /** FormState object from useForm */
    formState?: any;

    /** Any additional props forwarded verbatim to DynamicSearchSelect */
    [key: string]: any;
}

// ---------------------------------------------------------------------------
// Helper utilities
// ---------------------------------------------------------------------------

/**
 * Normalise `filterFields` into a uniform array of { formField, apiKey } entries.
 *
 * - string  `"company"`                    → [{ formField: 'company', apiKey: <filterKey> }]
 * - object  `{ company: 'company_id' }`    → [{ formField: 'company', apiKey: 'company_id' }]
 */
const normaliseFilterFields = (
    input: FilterFieldsInput,
    fallbackApiKey: string
): FilterFieldEntry[] => {
    if (typeof input === 'string') {
        return [{ formField: input, apiKey: fallbackApiKey }];
    }

    // Object – each key is a form field, each value is the API filter key
    return Object.entries(input).map(([formField, apiKey]) => ({
        formField,
        apiKey,
    }));
};

/**
 * Returns `true` when **every** value in the map is non-empty.
 */
const allFiltersFilled = (filters: FilterMap): boolean =>
    Object.values(filters).every((v) => {
        if (v === null || v === undefined || v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `DynamicDependentField` is a fully reusable, typed wrapper around
 * `DynamicSearchSelect` that:
 *
 * - Reads `control`, `watch`, and `setValue` from the nearest `FormProvider`.
 * - Watches one or more dependent fields (`filterFields`).
 * - Builds a `customeFilter` object from the watched values.
 * - Resets its own value and triggers a fresh option load whenever a watched
 *   field changes.
 * - Disables itself automatically while any required filter is empty.
 *
 * @example
 * // String – single dependency, uses filterKey for the API filter key
 * <DynamicDependentField
 *   name="department_id"
 *   fieldArrayName="add_payment_entry"
 *   filterFields="company"
 *   filterKey="company_id"
 *   apiType="department"
 * />
 *
 * @example
 * // Object – multiple dependencies with explicit API filter keys
 * <DynamicDependentField
 *   name="location_id"
 *   fieldArrayName="employee"
 *   filterFields={{ company: 'company_id', branch: 'branch_id' }}
 *   apiType="location"
 * />
 */
const DynamicDependentField: React.FC<DynamicDependentFieldProps> = (props) => {
    const formContext = useFormContext();

    // Prioritise props over context to allow manual override (essential for MFE)
    const control = props.formControl || props.control || formContext?.control;
    const watch = props.watch || formContext?.watch;
    const setValue = props.setValue || formContext?.setValue;
    const getFieldState = props.getFieldState || formContext?.getFieldState;
    const formState = props.formState || formContext?.formState;

    const hasMethods = !!(control && watch && setValue);

    if (!hasMethods) {
        // Fallback: render the select without dependency logic if form methods are missing
        return (
            <DynamicSearchSelect
                addType={props?.addType || props?.apiType}
                enable_footer={props?.enable_footer ?? true}
                {...props}
                {...(control ? { formControl: control } : {})}
            />
        );
    }

    return (
        <DynamicDependentFieldContent
            {...props}
            control={control}
            watch={watch}
            setValue={setValue}
            getFieldState={getFieldState}
            formState={formState}
        />
    );
};

/**
 * Inner component that performs the dependency logic.
 * It is only rendered when form methods are available.
 */
const DynamicDependentFieldContent: React.FC<
    DynamicDependentFieldProps & {
        control: any;
        watch: any;
        setValue: any;
        getFieldState: any;
        formState: any;
    }
> = ({ control, watch, setValue, getFieldState: _getFieldState, formState: _formState, ...props }) => {
    const {
        name,
        filterFields = 'company_id',
        filterKey = 'company_id',
        fieldArrayName,
        enable_footer = true,
        apiType,
    } = props;

    // -----------------------------------------------------------------------
    // Normalise filterFields into a uniform array of { formField, apiKey }
    // -----------------------------------------------------------------------
    const entries: FilterFieldEntry[] = useMemo(
        () => normaliseFilterFields(filterFields, filterKey),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(filterFields), filterKey]
    );

    /** Just the form field names – used for watching and change detection */
    const formFieldNames = useMemo(() => entries.map((e) => e.formField), [entries]);

    // -----------------------------------------------------------------------
    // Watch all dependent form field values
    // -----------------------------------------------------------------------
    const watchedValues = watch(formFieldNames.map((f) => `${fieldArrayName}.${f}`));

    const watchedValuesArray: any[] = useMemo(
        () => (Array.isArray(watchedValues) ? watchedValues : [watchedValues]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(watchedValues)]
    );

    // -----------------------------------------------------------------------
    // Build the filter map  { company: 1, branch: 2 }  (keyed by formField)
    // -----------------------------------------------------------------------
    const filters: FilterMap = useMemo(() => {
        return formFieldNames.reduce<FilterMap>((acc, field, index) => {
            acc[field] = watchedValuesArray[index];
            return acc;
        }, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(watchedValuesArray), formFieldNames.join(',')]);

    // -----------------------------------------------------------------------
    // Determine whether the field should be disabled
    // -----------------------------------------------------------------------
    const isDisabledDueToFilters = !allFiltersFilled(filters);
    const isFieldDisabled = props?.disabled || isDisabledDueToFilters;

    // -----------------------------------------------------------------------
    // Track previous filters to detect real changes and avoid infinite loops
    // -----------------------------------------------------------------------
    const prevFiltersRef = useRef<FilterMap | null>(null);
    const isFirstRender = useRef(true);

    // -----------------------------------------------------------------------
    // Reset own value when any filter changes (skip on mount)
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevFiltersRef.current = filters;
            return;
        }

        const prev = prevFiltersRef.current;
        const changedFields = formFieldNames.filter((field) => prev?.[field] !== filters[field]);
        const hasChanged = changedFields.length > 0;

        if (hasChanged) {
            prevFiltersRef.current = filters;

            setValue(`${fieldArrayName}.${name}`, props?.is_multiselect ? [] : null, {
                shouldValidate: false,
                shouldDirty: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters)]);

    // -----------------------------------------------------------------------
    // Build customeFilter in the format DynamicSearchSelect expects.
    //
    // For each entry, map the watched form value to the correct API filter key:
    //   { formField: 'company', apiKey: 'company_id', value: 1 }
    //     → { '&company_id.in': [1] }
    //
    // Array values (multiselect) are passed through as-is.
    // -----------------------------------------------------------------------
    const customeFilter: FilterMap = useMemo(() => {
        const dependentFilters = entries.reduce<FilterMap>((acc, entry) => {
            const value = filters[entry.formField];
            const hasValue = Array.isArray(value)
                ? value.length > 0
                : (value !== null && value !== undefined && value !== '');

            acc[`&${entry.apiKey}.in`] = hasValue ? Array.isArray(value) ? value : [value] : 0;
            return acc;
        }, {});

        // Merge with rest.customeFilter if provided
        return {
            ...dependentFilters,
            ...(props?.customeFilter || {})
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters), JSON.stringify(props?.customeFilter), isDisabledDueToFilters]);

    // -----------------------------------------------------------------------
    // Key derived from filter values — forces a full remount of DynamicSearchSelect
    // whenever any parent filter (e.g. company) changes, clearing its internal state.
    // -----------------------------------------------------------------------
    const updateKey = JSON.stringify(filters);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <DynamicSearchSelect
            key={updateKey}
            options={[]}
            addType={props?.addType || apiType}
            enable_footer={enable_footer}
            disabled={isFieldDisabled}
            {...(props as any)}
            customeFilter={customeFilter}
            formControl={control}
        />
    );
};

export default DynamicDependentField;
